import {customersApiClient} from '@/services/apiClient';

/**
 * TRIP CHAT — the passenger's side of the thread they share with their driver.
 *
 * ── ⚠️ FETCHING THE CONVERSATION MARKS MESSAGES READ ──────────────────────────────
 * This is the single most important thing about these three endpoints, and it is a side
 * effect with no way to opt out: `GET /conversation` marks the driver's messages read as
 * it returns them. There is no separate mark-as-read call.
 *
 * Two rules follow, and breaking either is silent:
 *   · NEVER poll the conversation to keep a badge fresh. Every poll clears the badge while
 *     nobody is looking at the thread. `unreadCount` exists precisely for that job.
 *   · NEVER prefetch a thread on hover or route preload, for the same reason.
 *
 * ── ⚠️ THE ENVELOPE NESTS INCONSISTENTLY ──────────────────────────────────────────
 * The mobile client probes several paths rather than assuming one, and so does this. A
 * field moving a level deeper in a future release should not be a production incident.
 */

/** The server's page size. Page 1 is the NEWEST messages on this side, unlike the driver's. */
export const CHAT_PAGE_SIZE = 20;

/** 1–5000 after trimming, enforced before the request so a whitespace-only send never
    costs a round trip and an oversized one never earns a guaranteed rejection. */
export const MESSAGE_MAX_LENGTH = 5000;

/** Read the first path that holds something. One helper, used everywhere a shape is read. */
function pick(source, paths) {
  for (const path of paths) {
    const value = path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), source);
    if (value !== undefined && value !== null) return value;
  }
  return null;
}

/**
 * One message, normalised.
 *
 * ⚠️ `sender` AND `created_at` ARE BOTH NULLABLE, and the type says so deliberately. A row
 * with no sender is rendered as the other party rather than blanked, and a null timestamp
 * is left empty rather than formatted into a confident, wrong "12:00".
 *
 * ⚠️ `sender_type` IS AN OPEN STRING. Branch on `=== 'passenger'` for your own bubbles and
 * treat everything else as someone else's, so a future 'support' or 'system' sender reads
 * as a message from them rather than as one of yours.
 */
export function parseMessage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id;
  if (id == null) return null;

  return {
    id,
    tripId: raw.trip_id ?? null,
    sender: raw.sender ?? null,
    senderType: raw.sender_type ?? '',
    isMine: raw.sender_type === 'passenger',
    message: String(raw.message ?? ''),
    isRead: !!raw.is_read,
    createdAt: raw.created_at ?? null,
    /* ⚠️ `TripMessageResource` carries NO `created_at` today — only `read_at`. It is the
       nearest thing to a send time the API gives (a read message was sent just before it
       was read), so the feed orders on it when `created_at` is absent. */
    readAt: raw.read_at ?? null,
  };
}

/**
 * HOW MANY PAGES THERE ARE, from whatever the envelope says.
 *
 * ⚠️ DERIVED WHEN IT IS NOT STATED. `last_page` is the direct answer, `total_pages` is
 * the same field under another name, and `total / per_page` reconstructs it when neither is
 * present. A thread whose page count is unknown is treated as one page, which is the safe
 * reading: it shows what arrived rather than walking pages that may not exist.
 */
function lastPageOf(pagination) {
  if (!pagination) return 1;
  const stated = Number(pagination.last_page ?? pagination.total_pages);
  if (Number.isFinite(stated) && stated > 0) return stated;

  const total = Number(pagination.total);
  const perPage = Number(pagination.per_page) || CHAT_PAGE_SIZE;
  if (Number.isFinite(total) && total > 0) return Math.max(1, Math.ceil(total / perPage));
  return 1;
}

/** The newest thing in a page, for comparing two of them. Ids are monotonic; timestamps
    are the fallback for a payload that renumbers. */
function freshnessOf(messages) {
  let best = -Infinity;
  for (const m of messages) {
    const id = Number(m?.id);
    if (Number.isFinite(id)) best = Math.max(best, id);
  }
  return best;
}

export const chatService = {
  /**
   * ONE PAGE OF THE THREAD, RAW.
   *
   * ⚠️ `pagination` IS NULLABLE. Treat its absence as "one page, no more" rather than as
   * an error — an empty conversation has nothing to paginate.
   */
  async getConversation(tripId, page = 1) {
    const result = await customersApiClient.get(
      `/trips/${tripId}/conversation?page=${page}&per_page=${CHAT_PAGE_SIZE}`,
    );
    if (!result.success) return {success: false, messages: [], pagination: null, error: result.error};

    const raw = result.data;
    const list = pick(raw, ['messages', 'data.messages', 'conversation.messages']) || [];
    const pagination = pick(raw, ['pagination', 'data.pagination', 'meta', 'data.meta']);

    return {
      success: true,
      messages: (Array.isArray(list) ? list : []).map(parseMessage).filter(Boolean),
      pagination: pagination || null,
      error: null,
    };
  },

  /**
   * THE NEWEST PAGE — WITHOUT ASSUMING WHICH END PAGE 1 IS.
   *
   * ── ⚠️ THE TWO SIDES' DOCS DISAGREE, AND THE CODE CANNOT SPLIT THE DIFFERENCE ───
   * The passenger integration guide says page 1 is the NEWEST messages. The driver app's
   * own code comment says the same endpoint "paginates oldest-first". Both were written by
   * people reading the same backend. Only one can be right, and a chat that guesses wrong
   * opens on a conversation from last week with the newest message unreachable — silently,
   * because a full page of real messages looks exactly like a correct answer.
   *
   * So this does what the mobile client does: it ASKS. One extra request at most, and only
   * when the answer is not already obvious.
   *
   *   · A short first page is the whole thread. No order to determine.
   *   · `last_page > 1` — fetch it and compare. Fresher than page 1 means oldest-first, and
   *     the last page is the one to show.
   *   · No page count — probe page 2. Fresher means oldest-first; walking forward from
   *     there is left to `loadMore`, which already knows which way to go.
   *
   * ⚠️ AND THE DIRECTION IS RETURNED, not hidden. The caller needs it: "older messages"
   * is page + 1 in one order and page - 1 in the other.
   *
   * @returns {{success, messages, page, lastPage, oldestFirst, error}}
   */
  async getLatestConversation(tripId) {
    const first = await this.getConversation(tripId, 1);
    if (!first.success) return {...first, page: 1, lastPage: 1, oldestFirst: false};

    const lastPage = lastPageOf(first.pagination);

    /* Everything fits on one page — whichever end the server calls page 1, this IS the
       thread, and `useTripChat` sorts it for display anyway. */
    if (lastPage <= 1 && first.messages.length < CHAT_PAGE_SIZE) {
      return {...first, page: 1, lastPage: 1, oldestFirst: false};
    }

    const probePage = lastPage > 1 ? lastPage : 2;
    const probe = await this.getConversation(tripId, probePage);
    /* ⚠️ A FAILED PROBE IS NOT AN ERROR. Page 1 is already in hand and is a perfectly
       good answer under the newest-first reading; refusing to show it because a second,
       optional request failed would turn a working thread into an error screen. */
    if (!probe.success || !probe.messages.length) {
      return {...first, page: 1, lastPage, oldestFirst: false};
    }

    const oldestFirst = freshnessOf(probe.messages) > freshnessOf(first.messages);
    return oldestFirst
      ? {...probe, page: probePage, lastPage: Math.max(lastPage, probePage), oldestFirst: true}
      : {...first, page: 1, lastPage, oldestFirst: false};
  },

  /**
   * Send one.
   *
   * ⚠️ VALIDATED BEFORE THE REQUEST. The mobile client refuses rather than sending, and
   * this mirrors it: an empty send is a wasted round trip and an oversized one is a
   * rejection that was knowable without asking.
   */
  async send(tripId, message) {
    const text = String(message ?? '').trim();
    if (!text) return {success: false, data: null, error: 'Type a message first.'};
    if (text.length > MESSAGE_MAX_LENGTH) {
      return {success: false, data: null, error: 'That message is too long.'};
    }

    const result = await customersApiClient.post(`/trips/${tripId}/messages`, {message: text});
    if (!result.success) return result;

    const created = pick(result.data, ['message', 'data.message']) || result.data;
    return {success: true, data: parseMessage(created), error: null};
  },

  /**
   * The badge.
   *
   * ⚠️ THE ONLY ENDPOINT SAFE TO CALL WHEN NOBODY IS READING. See the note at the top: the
   * conversation endpoint would clear the very number this is reporting.
   */
  async unreadCount(tripId) {
    const result = await customersApiClient.get(`/trips/${tripId}/messages/unread-count`);
    if (!result.success) return 0;
    const value = pick(result.data, ['unread_count', 'count', 'unread', 'data.unread_count']);
    return Number(value) || 0;
  },
};

export default chatService;
